import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Box, ButtonBase, makeStyles, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import moment from 'moment';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AddIcon, DocIcon, EditIcon, RemarksBgIcon } from '../../../components/Icon';
import SearchInput from '../../../components/SearchInput';
import StyleButton from '../../../components/StyleButton';
import { i18n } from '../../../language';
import TableUI from '../../../components/TableUI';
import LongRemark from '../../../components/LongRemark';
import { classNames, downloadFileById } from '../../../utils';
import DatePicker from '../../DutyAssign/components/Header/DatePicker';
import AutoCompleteMultiCheckbox from '../../../components/AutoCompleteMultiCheckbox';
import { equipmentAPI } from '../../../apis';
import { EQUIPMENT_REQUEST_DATE_FORMAT, REQUEST_STATUS_ENUM } from '../../../comm/constant';
import { authNameE } from '../../../utils/userFuncs';
import useUpdateEffect from '../../../utils/useUpdateEffect';
import Sort from '../../../components/Sort';
import { alertMsg } from '../../../apis/commlogic';
import NewLoanRequestDialog from './NewLoanRequestDialog';
import StatusBar from './StatusBar';
import EllipsisContent from './EllipsisContent';
import UpdateLoanRequestDialog from './UpdateLoanRequestDialog';
import NewLoanRequestModelBDialog from './NewLoanRequestDialog/modelB';
import UpdateLoanRequestModelBDialog from './UpdateLoanRequestDialog/modelB';
import HistoryLog from './HistoryLog';

const useStyle = makeStyles(() => ({
  root: {
    borderRadius: '0 0.937rem 0.937rem',
    backgroundColor: '#ffffff',
    padding: '1rem 1.875rem ',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  exportButton: {
    marginRight: '1.25rem',
  },
  headText: {
    fontSize: '1rem',
    fontWeight: '500',
    fontFamily: 'NotoSans-Medium',
    color: '#3e3e3e',
    marginRight: '0.5rem',
  },
  dateAutoSelect: {
    marginLeft: '0.5rem',
  },
  tableContent: {
    fontSize: '1.125rem',
    color: '#7d86a9',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '24rem',
  },
  editButton: {
    backgroundColor: '#4c93ff',
    borderRadius: '0.625rem',
    width: '2.25rem',
    height: '2.25rem',
  },
  editIcon: {
    fontSize: '1rem',
    color: '#ffffff',
  },
  tableStatus: {
    width: '0.625rem',
    minWidth: '0.625rem',
    height: '0.625rem',
    minHeight: '0.625rem',
    borderRadius: '50%',
    backgroundColor: '#34c759',
    marginRight: '0.625rem',
  },
  tab: {
    height: '2.5rem',
    width: '9.375rem',
    borderRadius: '20px',
    fontSize: '1rem',
  },
  tabSelect: {
    backgroundColor: '#538fff',
    fontFamily: 'NotoSans-Bold',
    color: '#fff',
  },
  tabUnSelect: {
    backgroundColor: '#f5faff',
    border: 'solid 1px #c0c6cb',
    color: '#c0c6cb',
  },
  datePicker: {
    '& .MuiInputBase-root': {
      marginRight: '0 !important',
    },
  },
  bar: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '0.919rem',
    color: '#7d86a9',
    margin: '0 0.5rem',
  },
  sortButton: {
    width: '1.5rem',
    height: '1.5rem',
    marginLeft: '0.5rem',
    borderRadius: '12px',
    backgroundColor: '#f5faff',
  },
  defaultButton: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#1973e6',
    textDecoration: 'underline',
  },
  tableMultCell: {
    borderBottom: 'none !important',
  },
  highlight: {
    color: '#B92B21',
  },
  markicon: {
    fontSize: '1.25rem',
    color: '#c0c6cb',
    marginLeft: '0.5rem',
  },
}));

const initFilter = {
  search: '',
  category: [],
  requestWard: [],
  status: [],
  startDate: moment().subtract(2, 'months'),
  endDate: null,
};

const initPageInfo = {
  page: 1,
  size: 50,
};

const getStatusData = (item, currentWardId) => {
  return _.find(REQUEST_STATUS_ENUM, (statusItem) => {
    if (item.status === 2 && currentWardId === item.oldRequestWardId) {
      return statusItem.enumKey === -1;
    } else if (item.status === 4 && currentWardId === item.oldRequestWardId) {
      return statusItem.enumKey === -2;
    }
    return statusItem.enumKey === item.status;
  });
};

const getRequestWardText = (item) => {
  return item?.roomName !== null
    ? `${item.requestHospCode}-${item.requestWard}-${item.roomName}`
    : `${item.requestHospCode}-${item.requestWard}`;
};

const initMultiCategory = (item, currentWardId) => {
  const resultData = item.categories.map((categoryItem, categoryIndex) => ({
    requestID: categoryIndex === 0 ? item.loanId : '',
    remark: categoryIndex === 0 ? item.remark : '',
    localcode: { assetIds: categoryItem.assetIds ?? [], localCodes: categoryItem.localCodes ?? [] },
    category: { title: categoryItem.categoryName, subTitle: categoryItem.models ?? [] },
    qty: categoryItem.qty ?? '',
    actQty: categoryItem.actQty ?? 0,
    // requestWard: categoryIndex === 0 ? getRequestWardText(item) : '',
    // provider:
    //   categoryIndex === 0
    //     ? `${item.provideHospCode}-${item.provideWard}${
    //         item.satelliteStoresName ? ` (${item.satelliteStoresName})` : ''
    //       }`
    //     : '',
    requestWard: categoryIndex === 0 ? item?.request : '',
    provider: item?.provide,
    status: categoryIndex === 0 ? item.status : '',
    date: categoryIndex === 0 ? item.date : '',
    edit: categoryIndex === 0,
    statusData: getStatusData(item, currentWardId),
    hiddenBorder: categoryIndex !== item.categories.length - 1,
    key: `${item.loanId}(${categoryIndex})`,
    transferHistoryLogList: item.transferHistoryLogList,
    loanDuration: item.loanDuration,
    highlight: item.highlight,
    diffDays: item.diffDays,
    satelliteStoresName: item.satelliteStoresName,
  }));

  return resultData.map((rowItem) => ({
    ...rowItem,
    editData: resultData[0],
  }));
};

const initSingleCategory = (item, currentWardId) => {
  return {
    requestID: item.loanId,
    remark: item.remark,
    localcode: {
      assetIds: item.categories[0]?.assetIds ?? [],
      localCodes: item.categories[0]?.localCodes ?? [],
    },
    category: { title: item.categories[0]?.categoryName ?? '', subTitle: item.categories[0]?.models ?? [] },
    qty: item.categories[0]?.qty ?? 0,
    actQty: item.categories[0]?.actQty ?? 0,
    // requestWard: getRequestWardText(item),
    // provider: `${item.provideHospCode}-${item.provideWard}${
    //   item.satelliteStoresName ? ` (${item.satelliteStoresName})` : ''
    // }`,
    requestWard: item?.request,
    provider: item?.provide,
    status: item.status,
    date: item.date,
    edit: true,
    statusData: _.find(REQUEST_STATUS_ENUM, (statusItem) => {
      if (item.status === 2 && currentWardId === item.oldRequestWardId) {
        return statusItem.enumKey === -1;
      } else if (item.status === 4 && currentWardId === item.oldRequestWardId) {
        return statusItem.enumKey === -2;
      }
      return statusItem.enumKey === item.status;
    }),
    hiddenBorder: false,
    key: item.loanId,
    transferredWardId: item?.transferredWardId,
    transferHistoryLogList: item.transferHistoryLogList,
    loanDuration: item.loanDuration,
    highlight: item.highlight,
    diffDays: item.diffDays,
    satelliteStoresName: item.satelliteStoresName,
  };
};

let overDuration = true;
const ViewRequestTab = ({
  refresh,
  setStopRefresh,
  newLendRequestOpen,
  tabFlagVal,
  setBarcodeListener,
  setTabFlagVal,
  setNewLendRequestOpen,
}) => {
  const classes = useStyle();
  const [newLoanRequestOpen, setNewLoanRequestOpen] = useState(false);
  const [tabFlag, setTabFlag] = useState(tabFlagVal.tab);
  const [, setBarcodeListenerCtrl] = useState(true);
  const loginInfo = useSelector((state) => state.loginInfo);
  const [sort, setSort] = useState({});
  const [filter, setFilter] = useState(initFilter);
  const [loanRequetData, setLoanRequetData] = useState(null);
  const [requestWard, setRequestWard] = useState(null);
  const [categories, setCategories] = useState(null);
  const [inputReset, setInputReset] = useState(false);
  const [editPopupData, setEditPopupData] = useState();
  const roleMap = useSelector((state) => state.roleMap);
  const [pageInfo, setPageInfo] = useState(initPageInfo);
  const location = useLocation();
  const [isShowTab, setIsShowTab] = useState(false);
  const [loading, setLoading] = useState(false);
  const rouList = useSelector((state) => state.rouList);
  const lastCallLoanRequestVersion = useRef(0);

  // 處理 表單API 和 export API 的參數
  const tableParamsHandler = useMemo(
    () => ({
      wardId: loginInfo.wardId,
      type: tabFlag,
      keyword: filter.search,
      categoryIds: _.map(filter.category, (item) => item.value),
      locationKeys: _.map(filter.requestWard, (item) => item.value),
      status: _.map(filter.status, (item) => item.value),
      startDate: moment(filter.startDate).format('yyyy-MM-DD'),
      endDate: moment(filter.endDate).isValid() ? moment(filter.endDate).format('yyyy-MM-DD') : null,
      sortInfo: sort,
      pageInfo: {
        pageNo: pageInfo.page,
        pageSize: pageInfo.size,
      },
    }),
    [filter, sort, loginInfo.wardId, pageInfo]
  );

  // 刷新頁面
  useUpdateEffect(() => {
    setPageInfo({ ...initPageInfo });
  }, [refresh]);

  // filter事件
  const filterHandler = (type) => (value) => {
    // 初始化翻頁信息
    setPageInfo(initPageInfo);

    // 處理相應的filter事件
    if (type === 'reset') {
      setFilter((p) => {
        return { ...initFilter, endDate: null };
      });
      setInputReset((preState) => !preState);
      setSort({});
    } else {
      console.log(type);
      console.log(value);
      setFilter((preFilter) => ({ ...preFilter, [type]: value }));
    }
  };

  // 表格的排序點擊事件
  const changeSortHandler = (type) => () => {
    // 初始化翻頁信息
    setPageInfo(initPageInfo);

    setSort((preSort) => {
      if (preSort.sortKey === type) {
        return { ...preSort, sortOrder: preSort.sortOrder === 'desc' ? 'asc' : 'desc' };
      } else {
        return { sortKey: type, sortOrder: 'desc' };
      }
    });
  };

  // export button 事件
  const exportHandler = useCallback(async () => {
    try {
      await downloadFileById(
        () =>
          equipmentAPI.getLoanRequestExport({
            search: tableParamsHandler,
            desc: {
              hospName: loginInfo.hospitalName,
              wardName: loginInfo.wardName,
              locationNames: _.map(filter.requestWard, (item) => item.text),
              categoryNames: _.map(filter.category, (item) => item.text),
              statusNames: _.map(filter.status, (item) => item.text),
            },
          }),
        () => `WardEquipment ViewRequest ${tabFlag === 1 ? 'Inbound' : 'Outbound'} Export Excel_${moment().valueOf()}`
      );
    } catch (error) {
      console.log(error);
    }
  }, [tableParamsHandler, tabFlag, loginInfo, filter]);

  // 獲取顯示哪個 tab
  const getTabAPI = useCallback(async () => {
    try {
      if (tabFlagVal.barcode) {
        setTabFlag(tabFlagVal.tab);
        setNewLoanRequestOpen(newLendRequestOpen);
      }
      const { result } = await equipmentAPI.getTab();
      setIsShowTab(result.isTab);
      if (!tabFlagVal.barcode) {
        setTabFlag(result.isTab ? 1 : 2);
      }
    } catch (error) {
      console.log(error);
    }
  }, [tabFlagVal, newLendRequestOpen]);

  useEffect(() => {
    (async () => {
      await getTabAPI();
    })();
  }, [getTabAPI, rouList]);

  useEffect(() => {
    setBarcodeListener(!newLoanRequestOpen);
  }, [newLoanRequestOpen]);
  // 獲取表單數據
  const getViewRequestList = useCallback(async () => {
      try {
        const thisCallLoanRequestVersion = ++lastCallLoanRequestVersion.current;
        setLoading(true);
        const { result } = await equipmentAPI.getViewRequestList(tableParamsHandler, { isHideLoading: true });
  
        // 區分翻頁和第一頁的數據
        if (thisCallLoanRequestVersion === lastCallLoanRequestVersion.current) {
          if (pageInfo.page === 1) {
            setLoanRequetData(result);
          } else {
            setLoanRequetData((preLoanRequestData) => ({ ...result, data: [...preLoanRequestData.data, ...result.data] }));
          }
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.log(error);
      }
  }, [tableParamsHandler, pageInfo]);

  // 獲取 ward list
  const getWardList = useCallback(async () => {
    try {
      const { result } = await equipmentAPI.getRequestWard({ wardId: loginInfo.wardId, type: tabFlag });
      setRequestWard(result);
    } catch (error) {
      console.log(error);
    }
  }, [loginInfo, tabFlag]);

  // 獲取 category list
  const getCategoryList = useCallback(async () => {
    try {
      const { result } = await equipmentAPI.getCategoryList({ hospitalId: loginInfo.hospitalId });

      setCategories(result.categories);
    } catch (error) {
      console.log(error);
    }
  }, [loginInfo]);

  useEffect(() => {
    (async () => {
      await getWardList();
    })();
    (async () => {
      await getCategoryList();
    })();
  }, [getWardList, getCategoryList]);

  useEffect(() => {
    (async () => {
      await getViewRequestList();
    })();
  }, [getViewRequestList]);

  useEffect(() => {
    if (location.query) {
      editDialogHandler('open')({ requestID: location.query.requestID, status: '' })();
    }
  }, []);

  // edit dialog 的開關
  const editDialogHandler = useCallback(
    (type) => (value) => {
      switch (type) {
        case 'close':
          setEditPopupData();
          setStopRefresh(false);
          break;
        case 'open':
          return () => {
            if (roleMap[authNameE.equipment_create_Loan_Request_Requestor_Provider_ward]) {
              setStopRefresh(true);
              setEditPopupData(value);
            }
          };
        case 'finish':
          setEditPopupData();
          setStopRefresh(false);
          setPageInfo({ ...initPageInfo });
          break;
        default:
          break;
      }
    },
    [getViewRequestList, pageInfo, roleMap[authNameE.equipment_create_Loan_Request_Requestor_Provider_ward]]
  );

  // 格式化表格數據
  const tableData = useMemo(() => {
    let data = [];
    let status = { pending: 0, loaned: 0, declined: 0, returned: 0, withdrawn: 0, transfer: 0 };
    if (loanRequetData) {
      status = {
        pending: loanRequetData.pengdingAmount,
        loaned: loanRequetData.loanedAmount,
        declined: loanRequetData.declinedAmount,
        returned: loanRequetData.returnedAmount,
        withdrawn: loanRequetData.withdrawnAmount,
        transfer: loanRequetData.transferAmount,
      };

      if (loanRequetData?.diffDays && overDuration) {
        alertMsg(
          `There are loan request(s) with loan duration > ${loanRequetData?.diffDays} days.`,
          'loan duration',
          'error',
          null,
          false
        );
      }

      overDuration = false;

      if (_.isArray(loanRequetData.data)) {
        data = _.map(loanRequetData.data, (item) => {
          if (item.categories.length > 1) {
            return initMultiCategory(item, loginInfo.wardId);
          } else {
            return initSingleCategory(item, loginInfo.wardId);
          }
        });
      }
    }

    return { data, status };
  }, [loanRequetData, loginInfo.wardId]);
  // filter dropdown 的 list item
  const itemLists = useMemo(() => {
    let statusList = _.map(REQUEST_STATUS_ENUM, (item) => ({
      text: i18n.t(item.textKey),
      value: item.enumKey,
      tagId: item.enumKey,
    }));
    let wardList = [];
    let categoryList = [];

    if (_.isArray(requestWard)) {
      wardList = _.map(requestWard, (item) => ({ text: item, value: item }));
    }

    if (_.isArray(categories)) {
      categoryList = _.map(categories, (item) => ({
        text: item.categoryName,
        value: item.categoryId,
        tagId: item.categoryId,
      }));
    }

    return { wardList, statusList, categoryList };
  }, [requestWard, categories, i18n.t()]);

  // new loan request dialog 的開關
  const newLoanRequestDialogHandler = useCallback(
    (type) => () => {
      switch (type) {
        case 'close':
          setBarcodeListenerCtrl(true);
          setNewLoanRequestOpen(false);
          setStopRefresh(false);
          setNewLendRequestOpen(false);
          break;
        case 'open':
          setBarcodeListenerCtrl(false);
          setNewLoanRequestOpen(true);
          setStopRefresh(true);
          break;
        case 'finish':
          setBarcodeListenerCtrl(true);
          setNewLoanRequestOpen(false);
          setStopRefresh(false);
          setPageInfo({ ...initPageInfo });
          setNewLendRequestOpen(false);
          break;
        default:
          break;
      }
    },
    [getViewRequestList, pageInfo]
  );

  // 切換 tab
  const tabHandler = (tab) => () => {
    setTabFlag(tab);
    // 初始化翻頁信息
    setPageInfo(initPageInfo);
  };

  // filter input 獲取焦點
  const inputOnFocus = useCallback(() => {
    setBarcodeListenerCtrl(false);
  }, []);

  // filter input 失去焦點
  const inputOnBlur = useCallback(() => {
    setBarcodeListenerCtrl(true);
  }, []);

  // 翻頁
  const turnPages = useCallback(() => {
    setPageInfo((preInfo) => {
      if (preInfo.page * preInfo.size < loanRequetData.count) {
        return {
          ...preInfo,
          page: preInfo.page + 1,
        };
      } else return preInfo;
    });
  }, [loanRequetData]);

  // model A && model B
  const dialogMemo = useMemo(() => {
    let NewRequest = tabFlag === 1 ? NewLoanRequestDialog : NewLoanRequestModelBDialog;
    let UpdateRequest = tabFlag === 1 ? UpdateLoanRequestDialog : UpdateLoanRequestModelBDialog;

    return {
      NewRequest,
      UpdateRequest,
    };
  }, [
    tabFlag,
    NewLoanRequestDialog,
    NewLoanRequestModelBDialog,
    UpdateLoanRequestDialog,
    UpdateLoanRequestModelBDialog,
  ]);

  // provider 在打开的时候
  const providerOnOpen = useCallback(() => {
    if (tabFlag === 2) {
      (async () => {
        await getWardList();
      })();
    }
  }, [tabFlag, getWardList]);

  // 切换 tab 的时候重置 filter 条件
  useUpdateEffect(() => {
    filterHandler('reset')();
  }, [tabFlag]);

  return (
    <div className={classes.root}>
      <Box display="flex" justifyContent="space-between" flexDirection="column">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb="0.8rem">
          <Box display="flex" alignItems="center">
            {isShowTab ? (
              <>
                <ButtonBase
                  className={classNames(classes.tab, tabFlag === 1 ? classes.tabSelect : classes.tabUnSelect)}
                  style={{ marginRight: '0.75rem' }}
                  onClick={tabHandler(1)}
                  id="tab-lend"
                >
                  {i18n.t('EQUIPMENT_VIEWREQUEST_INBOUND')}
                </ButtonBase>
                <ButtonBase
                  className={classNames(classes.tab, tabFlag === 2 ? classes.tabSelect : classes.tabUnSelect)}
                  onClick={tabHandler(2)}
                  id="tab-borrow"
                >
                  {i18n.t('EQUIPMENT_VIEWREQUEST_OUTBOUND')}
                </ButtonBase>
              </>
            ) : (
              <SearchInput
                width="23.438rem"
                height="2.563rem"
                placeholder={i18n.t('EQUIPMENT_VIEWREQUEST_SEARCH_PLACEHOLDER')}
                inputID="ViewRequest_search_input"
                buttonID="ViewRequest_search_button"
                clearID="ViewRequest_search_clear"
                onFocus={inputOnFocus}
                onBlur={inputOnBlur}
                onChange={filterHandler('search')}
                reset={inputReset}
                closeIcon={true}
              />
            )}
          </Box>
          <Box display="flex">
            {roleMap[authNameE.equipment_export_and_Reports] && (
              <StyleButton
                className={classes.exportButton}
                id="ViewRequest_export_button"
                text={i18n.t('EQUIPMENT_VIEWREQUEST_EXPORT')}
                Icon={DocIcon}
                onClick={() => {
                  (async () => {
                    await exportHandler();
                  })();
                }}
              />
            )}

            {roleMap[authNameE.equipment_create_Loan_Request_Requestor_Provider_ward] && (
              <StyleButton
                id="ViewRequest_newLoanRequest_button"
                text={
                  tabFlag === 1
                    ? i18n.t('EQUIPMENT_VIEWREQUEST_NEWLOANREQUEST')
                    : i18n.t('EQUIPMENT_VIEWREQUEST_NEWLOANREQUEST_B')
                }
                Icon={AddIcon}
                width="12.5rem"
                type="fill"
                iconStyle={{ fontSize: '1rem' }}
                onClick={newLoanRequestDialogHandler('open')}
              />
            )}
          </Box>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gridGap="0.8rem">
          {isShowTab && (
            <SearchInput
              width="23.438rem"
              height="2.563rem"
              placeholder={i18n.t('EQUIPMENT_VIEWREQUEST_SEARCH_PLACEHOLDER')}
              inputID="ViewRequest_search_input"
              buttonID="ViewRequest_search_button"
              clearID="ViewRequest_search_clear"
              onFocus={inputOnFocus}
              onBlur={inputOnBlur}
              onChange={filterHandler('search')}
              reset={inputReset}
              closeIcon={true}
            />
          )}
          <StatusBar data={tableData.status} />
        </Box>
      </Box>
      <Box display="flex" alignItems="center" flexWrap="wrap">
        <Box display="flex" alignItems="center" mt="0.8rem" mr="0.8rem">
          <div className={classes.headText}>{i18n.t('EQUIPMENT_VIEWREQUEST_FILTER')}</div>
          <AutoCompleteMultiCheckbox
            dropDownIcon={true}
            align="right"
            height="2.563rem"
            width="11.5rem"
            errMsgMargin="3px 0 0 0.825rem"
            errMsgWidth="9rem"
            allText={i18n.t('EQUIPMENT_VIEWREQUEST_ALL_CATEGORY')}
            itemList={itemLists.categoryList}
            value={filter.category}
            onChange={filterHandler('category')}
            type="equipment"
            id="AutoCompleteCheckbox-category"
          />
        </Box>
        <Box mt="0.8rem" mr="0.8rem">
          <AutoCompleteMultiCheckbox
            dropDownIcon={true}
            align="right"
            height="2.563rem"
            width="11.5rem"
            errMsgMargin="3px 0 0 0.825rem"
            errMsgWidth="9rem"
            allText={i18n.t(
              tabFlag === 1 ? 'EQUIPMENT_VIEWREQUEST_ALL_REQUEST_WARD' : 'EQUIPMENT_VIEWREQUEST_ALL_PROVIDER'
            )}
            itemList={itemLists.wardList}
            value={filter.requestWard}
            onChange={filterHandler('requestWard')}
            type="equipment"
            id="AutoCompleteCheckbox-ward"
            onOpen={providerOnOpen}
          />
        </Box>
        <Box mt="0.8rem" mr="0.8rem">
          <AutoCompleteMultiCheckbox
            dropDownIcon={true}
            align="right"
            height="2.563rem"
            width="11.5rem"
            errMsgMargin="3px 0 0 0.825rem"
            errMsgWidth="9rem"
            allText={i18n.t('EQUIPMENT_VIEWREQUEST_ALL_STATUS')}
            itemList={itemLists.statusList}
            value={filter.status}
            onChange={filterHandler('status')}
            type="equipment"
            id="AutoCompleteCheckbox-status"
            onlySelect={true}
          />
        </Box>
        <Box display="flex" alignItems="center" mt="0.8rem" mr="0.8rem">
          <DatePicker
            width="11.5rem"
            color="#3e3e3e"
            selectDate={filter.startDate}
            handleSelectDate={filterHandler('startDate')}
            className={classes.datePicker}
            id="start-date"
            placeholder={i18n.t('EHANDOVER_START_DATE')}
          />
          <div className={classes.bar}>-</div>
          <DatePicker
            width="11.5rem"
            color="#3e3e3e"
            selectDate={filter.endDate}
            handleSelectDate={filterHandler('endDate')}
            className={classes.datePicker}
            id="end-date"
            placeholder={i18n.t('EHANDOVER_END_DATE')}
            allowNull
          />
        </Box>
        <Box mt="0.8rem">
          <ButtonBase className={classes.defaultButton} onClick={filterHandler('reset')} id="reset">
            {i18n.t('EQUIPMENT_VIEWREQUEST_RESET')}
          </ButtonBase>
        </Box>
      </Box>
      <TableUI turnPages={turnPages} loading={loading} headPadding="0.8rem 1rem 1rem">
        <TableHead>
          <TableRow>
            <TableCell align="left" style={{ width: '10%' }}>
              {i18n.t('EQUIPMENT_VIEWREQUEST_TABLE_REQUEST')}
            </TableCell>
            <TableCell align="left" style={{ width: '10%' }}>
              {i18n.t('EQUIPMENT_VIEWREQUEST_TABLE_LOCALCODE')}
            </TableCell>
            <TableCell align="left" style={{ width: tabFlag === 1 ? '30%' : '20%' }}>
              <Box display="flex" alignItems="center">
                {i18n.t('EQUIPMENT_VIEWREQUEST_TABLE_CATEGORY')}
                <ButtonBase id="sort-category" className={classes.sortButton} onClick={changeSortHandler('category')}>
                  <Sort sort={sort} sortKey="category" />
                </ButtonBase>
              </Box>
            </TableCell>
            <TableCell align="left" style={{ width: '5%' }}>
              {i18n.t('EQUIPMENT_LOAN_DURATION_TITLE')}
              <ButtonBase id="sort-category" className={classes.sortButton} onClick={changeSortHandler('loanDuration')}>
                <Sort sort={sort} sortKey="loanDuration" />
              </ButtonBase>
            </TableCell>
            <TableCell align="left" style={{ width: '5%' }}>
              {i18n.t('EQUIPMENT_VIEWREQUEST_TABLE_QTY')}
            </TableCell>
            <TableCell align="left" style={{ width: '12%' }}>
              {i18n.t(tabFlag === 1 ? 'EQUIPMENT_VIEWREQUEST_TABLE_WARD' : 'EQUIPMENT_VIEWREQUEST_TABLE_PROVIDER')}
            </TableCell>
            {tabFlag === 1 && (
              <TableCell align="left" style={{ width: '12%' }}>
                {i18n.t('EQUIPMENT_VIEWREQUEST_TABLE_PROVIDER')}
              </TableCell>
            )}
            <TableCell align="left" style={{ width: '11%' }}>
              {i18n.t('EQUIPMENT_VIEWREQUEST_TABLE_STATUS')}
            </TableCell>
            <TableCell align="left" style={{ width: '12%', minWidth: '8rem' }}>
              <Box display="flex" alignItems="center">
                {i18n.t('EQUIPMENT_VIEWREQUEST_TABLE_DATE')}
                <ButtonBase id="sort-date" className={classes.sortButton} onClick={changeSortHandler('date')}>
                  <Sort sort={sort} sortKey="date" />
                </ButtonBase>
              </Box>
            </TableCell>
            {roleMap[authNameE.equipment_create_Loan_Request_Requestor_Provider_ward] && (
              <TableCell align="left" style={{ width: '5%' }} />
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {_.flatMap(tableData.data).map((item) => (
            <TableRow onDoubleClick={editDialogHandler('open')(item.editData ?? item)} key={item.key}>
              <TableCell className={item.hiddenBorder ? classes.tableMultCell : ''}>
                {item.requestID ? (
                  <Box display="flex" alignItems="center">
                    {item.requestID}
                    {item.remark && (
                      <LongRemark
                        content={`${i18n.t('EQUIPMENT_VIEWREQUEST_TABLE_REMARK')} ${item.remark}`}
                        width="100%"
                        minWidth="10rem"
                        id={`longremark-${item.index}`}
                      />
                    )}
                  </Box>
                ) : (
                  ''
                )}
              </TableCell>
              <TableCell>
                <Box display="flex" flexDirection="column">
                  <div>{item.localcode.assetIds.join(',')}</div>
                  <EllipsisContent maxWidth="25vw" color="#7d86a9" text={item.localcode.localCodes.join(',')} />
                </Box>
              </TableCell>
              <TableCell>
                <Box display="flex" flexDirection="column">
                  <div>{item.category.title}</div>
                  <EllipsisContent maxWidth="25vw" color="#7d86a9" text={item.category.subTitle.join(',')} />
                </Box>
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" className={item.highlight ? classes.highlight : ''}>
                  {item.diffDays ? `${item.diffDays} Day` : ''}
                  {item.highlight && (
                    <ButtonBase
                      onClick={() =>
                        alertMsg(
                          `There are loan request(s) with loan duration > ${item.diffDays} days.`,
                          'loan duration',
                          'error',
                          null,
                          false
                        )
                      }
                    >
                      <RemarksBgIcon className={classes.markicon} />
                    </ButtonBase>
                  )}
                </Box>
              </TableCell>
              <TableCell>{`${item.actQty}/${item.qty}`}</TableCell>
              <TableCell>
                <Box display="flex" alignItems="center">
                  {tabFlag === 1 ? item.requestWard : item.provider}
                  {item?.transferHistoryLogList?.length > 0 && (
                    <HistoryLog transferHistoryLogList={item.transferHistoryLogList} />
                  )}
                </Box>
              </TableCell>
              {tabFlag === 1 && (
                <TableCell>
                  <Box display="flex" alignItems="center">
                    {item.provider}
                  </Box>
                </TableCell>
              )}
              <TableCell>
                {item.statusData ? (
                  <Box display="flex" alignItems="center">
                    <div className={classes.tableStatus} style={{ backgroundColor: item.statusData.color }} />
                    {i18n.t(item.statusData.textKey)}
                  </Box>
                ) : null}
              </TableCell>
              <TableCell>{item.date ? moment(item.date).format(EQUIPMENT_REQUEST_DATE_FORMAT) : ''}</TableCell>
              <TableCell>
                {item.edit ? (
                  <ButtonBase
                    id={`edit-${item.index}`}
                    className={classes.editButton}
                    onClick={editDialogHandler('open')(item.editData ?? item)}
                  >
                    <EditIcon className={classes.editIcon} />
                  </ButtonBase>
                ) : (
                  ''
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </TableUI>
      {newLoanRequestOpen && (
        <dialogMemo.NewRequest
          dialogCloseHandler={newLoanRequestDialogHandler('close')}
          dialogFinishHandler={newLoanRequestDialogHandler('finish')}
        />
      )}
      {editPopupData && (
        <dialogMemo.UpdateRequest
          data={editPopupData}
          dialogCloseHandler={editDialogHandler('close')}
          dialogFinishHandler={editDialogHandler('finish')}
          categories={categories}
        />
      )}
    </div>
  );
};

export default ViewRequestTab;
